import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  View as RNView,
  Modal as RNModal,
  useWindowDimensions,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import DropDownPicker from "react-native-dropdown-picker";
import { useSelector, useDispatch } from "react-redux";
import reportsActions from "../../../redux/actions/reportsActions";
import { useColumnResize } from "../hooks/useColumnResize";
import { valueL36MModel } from "../../../models/reportsModel";
import { getValueL36M } from "../../../services/inventoryService";
import AsyncStorage from '@react-native-async-storage/async-storage';

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const COL_ITEM_W = 220;
const COL_CUSTOMER_W = 180;
const COL_SLP_W = 130;
const COL_TOTAL_W = 70;
const FIXED_COLS_W = COL_ITEM_W + COL_CUSTOMER_W + COL_SLP_W;
const MIN_MONTH_W = 52;

const getRowTotal = (row: valueL36MModel) =>
  MONTHS.reduce((sum, m) => sum + ((row[m as keyof valueL36MModel] as number) || 0), 0);

function toItems(values: string[]) {
  return values.sort().map((v) => ({ label: v, value: v }));
}

// ── Filter dropdown wrapper ───────────────────────────────────────────────────
interface FilterDropdownProps {
  label: string;
  value: string[];
  onChange: React.Dispatch<React.SetStateAction<string[]>>;
  items: { label: string; value: string }[];
  open?: boolean;
  setOpen?: (o: boolean) => void;
  zIndex?: number;
  zIndexInverse?: number;
  onOpen?: () => void;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label, value, onChange, items,
  open, setOpen, zIndex, zIndexInverse, onOpen,
}) => (
  <View style={[filterItemStyle, { zIndex }]}>
    {Platform.OS === "web" && (
      <style type="text/css">{`
        [data-hoveritem="true"]:hover, [data-hover-item="true"]:hover {
          background-color: #f5f5f5 !important;
        }
      `}</style>
    )}
    <Text style={filterLabelStyle}>{label}</Text>
    <DropDownPicker
      multiple={true}
      mode="BADGE"
      open={open!}
      setOpen={setOpen as any}
      items={items}
      value={value}
      setValue={onChange as any}
      placeholder="All"
      listMode="SCROLLVIEW"
      maxHeight={600}
      style={{ borderColor: "#ddd", borderRadius: 6, minHeight: 38 }}
      dropDownContainerStyle={{ borderColor: "#ddd", borderRadius: 6 }}
      zIndex={zIndex}
      zIndexInverse={zIndexInverse}
      onOpen={onOpen}
      badgeDotColors={["#1976d2"]}
      searchable={true}
      searchPlaceholder="Search..."
      searchContainerStyle={{ borderBottomColor: "#f0f0f0", borderBottomWidth: 1, padding: 10 }}
      searchTextInputStyle={{ borderColor: "#ddd", borderWidth: 1, borderRadius: 6, height: 36, paddingHorizontal: 12, fontSize: 13 }}
      itemProps={{ dataSet: { hoveritem: "true" } } as any}
      dropDownDirection="BOTTOM"
    />
  </View>
);

const filterItemStyle = { minWidth: 160, flex: 1 } as const;
const filterLabelStyle = { fontSize: 12, color: "#555", marginBottom: 4, fontWeight: "500" as const };

function getCacheWindowStart(): string {
  const now = new Date();
  const start = new Date(now);
  start.setHours(now.getHours() < 12 ? 0 : 12, 0, 0, 0);
  return start.toLocaleString("en-AU", { hour12: true });
}

const rszStyle = { position: "absolute" as const, right: 0, top: 0, bottom: 0, width: 6, cursor: "col-resize", zIndex: 2 };

// ── Main screen ───────────────────────────────────────────────────────────────
export default function ValueSalesScorecardScreen() {
  const dispatch = useDispatch();
  const { valueL36MReport, isValueL36MReportListLoaded, valueL36MHiddenColumns } = useSelector(
    (state: any) => state.reports
  );
  const rows: valueL36MModel[] = valueL36MReport;
  const hiddenCols = valueL36MHiddenColumns || [];

  useEffect(() => {
    getValueL36M();
  }, []);

  // ── Horizontal scroll sync ────────────────────────────────────────────────
  const scrollAnim = useRef(new Animated.Value(0)).current;
  const negScrollAnim = useRef(Animated.multiply(scrollAnim, -1)).current;
  const onBodyHScroll = useRef(
    Animated.event(
      [{ nativeEvent: { contentOffset: { x: scrollAnim } } }],
      { useNativeDriver: false }
    )
  ).current;

  // ── Data area height ──────────────────────────────────────────────────────
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [dataAreaHeight, setDataAreaHeight] = useState(() => Math.max(200, windowHeight - 380));
  const onDataAreaLayout = useCallback((e: any) => {
    setDataAreaHeight(e.nativeEvent.layout.height);
  }, []);

  const defaultColWidths = useMemo(() => {
    const visibleFixedW = (hiddenCols.includes("ItemName") ? 0 : COL_ITEM_W) + 
                          (hiddenCols.includes("Customer") ? 0 : COL_CUSTOMER_W) + 
                          (hiddenCols.includes("SlpName") ? 0 : COL_SLP_W);
    const visibleTotalW = hiddenCols.includes("Total") ? 0 : COL_TOTAL_W;
    const visibleMonths = MONTHS.filter(m => !hiddenCols.includes(m));
    const mw = visibleMonths.length > 0 
      ? Math.max(MIN_MONTH_W, Math.floor((windowWidth - 48 - visibleFixedW - visibleTotalW) / visibleMonths.length))
      : MIN_MONTH_W;

    const obj: Record<string, number> = { ItemName: COL_ITEM_W, Customer: COL_CUSTOMER_W, SlpName: COL_SLP_W, Total: COL_TOTAL_W };
    for (const m of MONTHS) obj[m] = mw;
    return obj;
  }, [windowWidth, hiddenCols]);

  const [colWidths, setColWidths] = useState<Record<string, number>>(defaultColWidths);
  useEffect(() => {
    setColWidths((prev) => {
      const obj: Record<string, number> = { ...prev };
      for (const m of MONTHS) obj[m] = defaultColWidths[m];
      return obj;
    });
  }, [defaultColWidths]);
  const startResize = useColumnResize(setColWidths);
  const tableWidth = Object.entries(colWidths).reduce((s, [k, w]) => hiddenCols.includes(k) ? s : s + w, 0);

  // ── Copy to clipboard ─────────────────────────────────────────────────────
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyCell = useCallback((key: string, text: string) => {
    if (Platform.OS === "web" && typeof navigator !== "undefined") {
      try { navigator.clipboard?.writeText(text); } catch { }
    }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1200);
  }, []);

  const CellWrapper: any = Platform.OS === "web" ? View : TouchableOpacity;

  // ── Filter open state (native only) ──────────────────────────────────────
  const [yearOpen, setYearOpen] = useState(false);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [slpOpen, setSlpOpen] = useState(false);
  const [itemGroupOpen, setItemGroupOpen] = useState(false);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [settingsBtnPos, setSettingsBtnPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const settingsBtnRef = useRef<any>(null);

  const allColOptions = useMemo(() => [
    { label: "Item Name", value: "ItemName" },
    { label: "Customer", value: "Customer" },
    { label: "Sales Person", value: "SlpName" },
    ...MONTHS.map(m => ({ label: m, value: m })),
    { label: "Total", value: "Total" }
  ], []);

  const columnOrder = useSelector((state: any) => state.reports.valueL36MColumnOrder) || [];

  const getOrderedKeys = useCallback((allKeys: string[]) => {
    if (columnOrder.length === 0) return allKeys;
    const ordered: string[] = [];
    columnOrder.forEach((k: string) => { if (allKeys.includes(k)) ordered.push(k); });
    allKeys.forEach((k: string) => { if (!ordered.includes(k)) ordered.push(k); });
    return ordered;
  }, [columnOrder]);

  const moveColumn = useCallback((fromKey: string, toKey: string) => {
    const allKeys = allColOptions.map((c) => c.value);
    const currentKeys = getOrderedKeys(allKeys).filter((k) => k !== "");
    const fromIdx = currentKeys.indexOf(fromKey);
    const toIdx = currentKeys.indexOf(toKey);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const newKeys = [...currentKeys];
    newKeys.splice(fromIdx, 1);
    newKeys.splice(toIdx, 0, fromKey);
    dispatch(reportsActions.setValueL36MColumnOrder(newKeys));
    AsyncStorage.setItem("value_scorecard_column_order", JSON.stringify(newKeys));
  }, [columnOrder, allColOptions, getOrderedKeys, dispatch]);

  const resetColumnOrder = () => {
    dispatch(reportsActions.setValueL36MColumnOrder([]));
    dispatch(reportsActions.setValueL36MHiddenColumns([]));
    AsyncStorage.removeItem("value_scorecard_column_order");

    setColWidths(defaultColWidths);
  };

  useEffect(() => {
    if (columnOrder.length === 0) {
      AsyncStorage.getItem("value_scorecard_column_order").then((val) => {
        if (val) {
          try { 
            const parsed = JSON.parse(val);
            if (parsed && parsed.length > 0) {
              dispatch(reportsActions.setValueL36MColumnOrder(parsed));
            }
          } catch {}
        }
      });
    }
  }, []);

  const [dragActiveKey, setDragActiveKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const dragRef = useRef<{ activeKey: string | null; overKey: string | null }>({ activeKey: null, overKey: null });

  const startColumnDrag = useCallback((key: string) => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    dragRef.current.activeKey = key;
    setDragActiveKey(key);

    const onMouseMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const row = el && (el as HTMLElement).closest ? (el as HTMLElement).closest("[data-columnkey]") : null;
      const overKey = row ? row.getAttribute("data-columnkey") : null;
      if (overKey !== dragRef.current.overKey) {
        dragRef.current.overKey = overKey;
        setDragOverKey(overKey && overKey !== dragRef.current.activeKey ? overKey : null);
      }
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      (document.body.style as any).userSelect = "";
      const from = dragRef.current.activeKey;
      const to = dragRef.current.overKey;
      if (from && to && from !== to) {
        moveColumn(from, to);
      }
      dragRef.current = { activeKey: null, overKey: null };
      setDragActiveKey(null);
      setDragOverKey(null);
    };

    document.body.style.cursor = "grabbing";
    (document.body.style as any).userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [moveColumn]);

  const toggleColumn = (columnKey: string) => {
    const updated = hiddenCols.includes(columnKey)
      ? hiddenCols.filter((k: string) => k !== columnKey)
      : [...hiddenCols, columnKey];
    dispatch(reportsActions.setValueL36MHiddenColumns(updated));
  };

  const availableYears = useMemo(
    () => Array.from(new Set(rows.map((r) => String(r.Year)).filter(Boolean))).sort().reverse(),
    [rows]
  );

  // ── Filter values (empty array = no filter / show all) ───────────────────
  const [yearVal, setYearVal] = useState<string[]>([]);
  const [dispatchVal, setDispatchVal] = useState<string[]>([]);
  const [deliveryVal, setDeliveryVal] = useState<string[]>([]);
  const [slpVal, setSlpVal] = useState<string[]>([]);
  const [itemGroupVal, setItemGroupVal] = useState<string[]>([]);
  const [customerVal, setCustomerVal] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const yearItems = useMemo(() => toItems(availableYears), [availableYears]);
  const dispatchItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r["Dispatch State"]).filter(Boolean)))), [rows]);
  const deliveryItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r["Delivery State"]).filter(Boolean)))), [rows]);
  const slpItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r.SlpName).filter(Boolean)))), [rows]);
  const itemGroupItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r["Item Group"]).filter(Boolean)))), [rows]);
  const customerItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r.Customer).filter(Boolean)))), [rows]);

  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const onSortPress = (col: string) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
    setCurrentPage(1);
    setExpandedRow(null);
  };

  const sortIndicator = (col: string) =>
    sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (yearVal.length > 0 && !yearVal.includes(String(r.Year))) return false;
      if (dispatchVal.length > 0 && !dispatchVal.includes(r["Dispatch State"])) return false;
      if (deliveryVal.length > 0 && !deliveryVal.includes(r["Delivery State"])) return false;
      if (slpVal.length > 0 && !slpVal.includes(r.SlpName)) return false;
      if (itemGroupVal.length > 0 && !itemGroupVal.includes(r["Item Group"])) return false;
      if (customerVal.length > 0 && !customerVal.includes(r.Customer)) return false;
      if (q) {
        const matchItem = r.ItemName?.toLowerCase().includes(q);
        const matchCustomer = r.Customer?.toLowerCase().includes(q);
        const matchSlp = r.SlpName?.toLowerCase().includes(q);
        if (!matchItem && !matchCustomer && !matchSlp) return false;
      }
      return true;
    });
  }, [rows, yearVal, dispatchVal, deliveryVal, slpVal, itemGroupVal, customerVal, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [yearVal, dispatchVal, deliveryVal, slpVal, itemGroupVal, customerVal, searchQuery]);

  const colTotals = useMemo(() => {
    const t: Record<string, number> = {};
    for (const m of MONTHS) {
      t[m] = filtered.reduce((sum, r) => sum + ((r[m as keyof valueL36MModel] as number) || 0), 0);
    }
    t["Total"] = MONTHS.reduce((sum, m) => sum + (t[m] || 0), 0);
    return t;
  }, [filtered]);

  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    return [...filtered].sort((a, b) => {
      const aVal = sortCol === "Total" ? getRowTotal(a) : a[sortCol as keyof valueL36MModel];
      const bVal = sortCol === "Total" ? getRowTotal(b) : b[sortCol as keyof valueL36MModel];
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal ?? "").toLowerCase();
      const bStr = String(bVal ?? "").toLowerCase();
      return sortDir === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [filtered, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageRows = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  const goToPage = (p: number) => { setCurrentPage(Math.max(1, Math.min(p, totalPages))); setExpandedRow(null); };
  const onPageSizeChange = (size: number) => { setPageSize(size); setCurrentPage(1); setExpandedRow(null); };

  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const toggleRow = (key: number) => setExpandedRow((prev) => (prev === key ? null : key));

  const colItem = [styles.colBase, { width: colWidths.ItemName }, hiddenCols.includes("ItemName") && { display: "none" as const }];
  const colCustomer = [styles.colBase, { width: colWidths.Customer }, hiddenCols.includes("Customer") && { display: "none" as const }];
  const colSlp = [styles.colBase, { width: colWidths.SlpName }, hiddenCols.includes("SlpName") && { display: "none" as const }];

  if (!isValueL36MReportListLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#1976d2" />
        <Text style={styles.loadingText}>Loading Value Sales Scorecard...</Text>
      </View>
    );
  }

  const anyOpen = yearOpen || dispatchOpen || deliveryOpen || slpOpen || itemGroupOpen || customerOpen;
  const closeAllDropdowns = () => {
    setYearOpen(false);
    setDispatchOpen(false);
    setDeliveryOpen(false);
    setSlpOpen(false);
    setItemGroupOpen(false);
    setCustomerOpen(false);
  };

  const resetFilters = () => {
    setYearVal([]);
    setDispatchVal([]);
    setDeliveryVal([]);
    setSlpVal([]);
    setItemGroupVal([]);
    setCustomerVal([]);
    setSearchQuery("");
  };

  return (
    <View style={styles.container}>
      {anyOpen && (
        <Pressable
          style={{ position: Platform.OS === "web" ? ("fixed" as any) : "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999, height: Platform.OS === "web" ? ("100vh" as any) : 2000 }}
          onPress={closeAllDropdowns}
        />
      )}
      {/* ── Frozen header: title + search + filters ────────────────────────── */}
      <View style={styles.frozenHeader}>
        <View style={styles.filterPanel}>
          <View style={styles.filterHeaderRow}>
            <Text style={styles.filterHeading}>Filters</Text>
            <TouchableOpacity onPress={resetFilters} style={styles.resetBtn}>
              <Text style={styles.resetBtnText}>Reset Filter</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.filterGrid}>
            <FilterDropdown
              label="Year"
              value={yearVal} onChange={setYearVal} items={yearItems}
              open={yearOpen} setOpen={setYearOpen} zIndex={700} zIndexInverse={100}
              onOpen={() => { setDispatchOpen(false); setDeliveryOpen(false); setSlpOpen(false); setItemGroupOpen(false); setCustomerOpen(false); }}
            />
            <FilterDropdown
              label="Sales Person"
              value={slpVal} onChange={setSlpVal} items={slpItems}
              open={slpOpen} setOpen={setSlpOpen} zIndex={600} zIndexInverse={200}
              onOpen={() => { setYearOpen(false); setDispatchOpen(false); setDeliveryOpen(false); setItemGroupOpen(false); setCustomerOpen(false); }}
            />
            <FilterDropdown
              label="Dispatch State"
              value={dispatchVal} onChange={setDispatchVal} items={dispatchItems}
              open={dispatchOpen} setOpen={setDispatchOpen} zIndex={500} zIndexInverse={300}
              onOpen={() => { setYearOpen(false); setDeliveryOpen(false); setSlpOpen(false); setItemGroupOpen(false); setCustomerOpen(false); }}
            />
            <FilterDropdown
              label="Delivery State"
              value={deliveryVal} onChange={setDeliveryVal} items={deliveryItems}
              open={deliveryOpen} setOpen={setDeliveryOpen} zIndex={400} zIndexInverse={400}
              onOpen={() => { setYearOpen(false); setDispatchOpen(false); setSlpOpen(false); setItemGroupOpen(false); setCustomerOpen(false); }}
            />
            <FilterDropdown
              label="Item Group"
              value={itemGroupVal} onChange={setItemGroupVal} items={itemGroupItems}
              open={itemGroupOpen} setOpen={setItemGroupOpen} zIndex={300} zIndexInverse={500}
              onOpen={() => { setYearOpen(false); setDispatchOpen(false); setDeliveryOpen(false); setSlpOpen(false); setCustomerOpen(false); }}
            />
            <FilterDropdown
              label="Customer"
              value={customerVal} onChange={setCustomerVal} items={customerItems}
              open={customerOpen} setOpen={setCustomerOpen} zIndex={200} zIndexInverse={600}
              onOpen={() => { setYearOpen(false); setDispatchOpen(false); setDeliveryOpen(false); setSlpOpen(false); setItemGroupOpen(false); setColumnsOpen(false); }}
            />
          </View>
        </View>
      </View>

      {/* ── Table section ──────────────────────────────────────────────────── */}
      <View style={styles.tableSection}>
        <View style={styles.tableCard}>

          {/* Top bar */}
          <View style={styles.tableTopBar}>
            <View style={{ flexDirection: "row", alignItems: "baseline" }}>
              <Text style={styles.tableTitle}>
                Monthly Breakdown — {filtered.length.toLocaleString()} rows
              </Text>
              <Text style={[styles.fetchedAt, { marginBottom: 0, marginLeft: 12 }]}>
                Data up to: {getCacheWindowStart()}
              </Text>
              <Text style={[styles.currencyIndicator, { marginBottom: 0, marginLeft: 12 }]}>
                || $AUD excl. GST
              </Text>
            </View>
            <View style={styles.pageSizeRow}>
              {(sortCol !== null || JSON.stringify(colWidths) !== JSON.stringify(defaultColWidths)) && (
                <TouchableOpacity 
                  onPress={() => {
                    setSortCol(null);
                    setColWidths(defaultColWidths);
                  }} 
                  style={styles.resetSortBtn}
                >
                  <Text style={styles.resetSortTxt}>Reset</Text>
                </TouchableOpacity>
              )}
              <RNView style={{ marginRight: 12 }}>
                <TouchableOpacity
                  ref={settingsBtnRef}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    backgroundColor: columnsOpen ? "#E0E7FF" : "#F3F4F6",
                    justifyContent: "center",
                    alignItems: "center",
                    borderWidth: 1,
                    borderColor: columnsOpen ? "#818CF8" : "#E2E8F0",
                  }}
                  onPress={() => {
                    if (settingsBtnRef.current) {
                      settingsBtnRef.current.measureInWindow(
                        (x: number, y: number, width: number, height: number) => {
                          setSettingsBtnPos({ x, y, width, height });
                          setColumnsOpen(!columnsOpen);
                        }
                      );
                    } else {
                      setColumnsOpen(!columnsOpen);
                    }
                  }}
                >
                  <Feather
                    name="settings"
                    size={14}
                    color={columnsOpen ? "#56B7FD" : "#6B7280"}
                  />
                </TouchableOpacity>
                {columnsOpen && (
                  <RNModal
                    transparent={true}
                    visible={columnsOpen}
                    animationType="fade"
                    onRequestClose={() => setColumnsOpen(false)}
                  >
                    <Pressable
                      style={{
                        flex: 1,
                        backgroundColor: "rgba(0,0,0,0.05)",
                      }}
                      onPress={() => setColumnsOpen(false)}
                    >
                      <Pressable
                        style={{
                          position: "absolute",
                          top: settingsBtnPos.y + settingsBtnPos.height + 6,
                          left: settingsBtnPos.x - 188, // shift left so it doesn't clip screen
                          backgroundColor: "#fff",
                          borderRadius: 8,
                          borderWidth: 1,
                          borderColor: "#E2E8F0",
                          paddingVertical: 10,
                          minWidth: 220,
                          shadowColor: "#000",
                          shadowOffset: { width: 0, height: 8 },
                          shadowOpacity: 0.15,
                          shadowRadius: 20,
                          elevation: 10,
                        }}
                        onPress={(e) => e.stopPropagation()}
                      >
                        <Text
                          style={{
                            fontSize: 11,
                            fontWeight: "700",
                            color: "#6B7280",
                            paddingHorizontal: 16,
                            paddingBottom: 8,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Show / Hide Columns
                        </Text>
                        <RNView style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 4 }} />
                        <TouchableOpacity
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            paddingVertical: 8,
                            paddingHorizontal: 16,
                          }}
                          onPress={() => {
                            if (hiddenCols.length === 0) {
                              dispatch(reportsActions.setValueL36MHiddenColumns(allColOptions.map((c) => c.value)));
                            } else {
                              dispatch(reportsActions.setValueL36MHiddenColumns([]));
                            }
                          }}
                        >
                          <RNView
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: 4,
                              borderWidth: 1.5,
                              borderColor: hiddenCols.length === 0 ? "#56B7FD" : "#D1D5DB",
                              backgroundColor: hiddenCols.length === 0 ? "#56B7FD" : "#fff",
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: 10,
                            }}
                          >
                            {hiddenCols.length === 0 && (
                              <Feather name="check" size={12} color="#fff" />
                            )}
                          </RNView>
                          <Text
                            style={{
                              fontSize: 13,
                              color: "#111827",
                              fontWeight: "700",
                            }}
                          >
                            Select All
                          </Text>
                        </TouchableOpacity>
                        <RNView style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 2 }} />
                        <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                          {getOrderedKeys(allColOptions.map(c => c.value)).map((colValue) => {
                            const col = allColOptions.find(c => c.value === colValue);
                            if (!col) return null;
                            const isVisible = !hiddenCols.includes(col.value);
                            const isDragging = dragActiveKey === col.value;
                            const isDragOver = dragOverKey === col.value;
                            return (
                              <RNView
                                key={col.value}
                                {...{ dataSet: { columnkey: col.value } } as any}
                                style={{
                                  flexDirection: "row",
                                  alignItems: "center",
                                  paddingVertical: 8,
                                  paddingHorizontal: 16,
                                  opacity: isDragging ? 0.35 : 1,
                                  backgroundColor: isDragOver ? "#EFF6FF" : "transparent",
                                  borderTopWidth: isDragOver ? 2 : 0,
                                  borderTopColor: "#56B7FD",
                                }}
                              >
                                <Pressable
                                  onPressIn={() => startColumnDrag(col.value)}
                                  style={{ padding: 4, marginRight: 6, cursor: "grab" } as any}
                                >
                                  <Feather name="menu" size={12} color="#CBD5E1" />
                                </Pressable>
                                <TouchableOpacity
                                  style={{ flexDirection: "row", alignItems: "center", flex: 1 }}
                                  onPress={() => toggleColumn(col.value)}
                                >
                                  <RNView
                                    style={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: 4,
                                      borderWidth: 1.5,
                                      borderColor: isVisible ? "#56B7FD" : "#D1D5DB",
                                      backgroundColor: isVisible ? "#56B7FD" : "#fff",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      marginRight: 10,
                                    }}
                                  >
                                    {isVisible && (
                                      <Feather name="check" size={12} color="#fff" />
                                    )}
                                  </RNView>
                                  <Text style={{ fontSize: 13, color: isVisible ? "#111827" : "#9CA3AF", fontWeight: isVisible ? "500" : "400" }}>
                                    {col.label}
                                  </Text>
                                </TouchableOpacity>
                              </RNView>
                            );
                          })}
                        </ScrollView>
                        {(columnOrder.length > 0 || hiddenCols.length > 0) && (
                          <>
                            <RNView style={{ height: 1, backgroundColor: "#F1F5F9", marginTop: 4 }} />
                            <TouchableOpacity
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                paddingVertical: 10,
                                paddingHorizontal: 16,
                              }}
                              onPress={resetColumnOrder}
                            >
                              <Feather name="rotate-ccw" size={13} color="#6B7280" />
                              <Text style={{ fontSize: 13, color: "#6B7280", fontWeight: "600", marginLeft: 6 }}>
                                Reset Layout
                              </Text>
                            </TouchableOpacity>
                          </>
                        )}
                      </Pressable>
                    </Pressable>
                  </RNModal>
                )}
              </RNView>
              <TextInput
                style={styles.searchInput}
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Search Bar"
                placeholderTextColor="#aaa"
                clearButtonMode="while-editing"
                onFocus={closeAllDropdowns}
              />
              <Text style={styles.pageSizeLabel}>Rows per page:</Text>
              {[50, 100, 200].map((n) => (
                <TouchableOpacity
                  key={n}
                  onPress={() => onPageSizeChange(n)}
                  style={[styles.pageSizeBtn, pageSize === n && styles.pageSizeBtnActive]}
                >
                  <Text style={[styles.pageSizeTxt, pageSize === n && styles.pageSizeTxtActive]}>
                    {n}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* ── Frozen column header ──────────────────────────────────────── */}
          <View style={{ overflow: "hidden" }}>
            <Animated.View
              style={[styles.tableHeader, { width: tableWidth, transform: [{ translateX: negScrollAnim }] }]}
            >
              {getOrderedKeys(allColOptions.map(c => c.value)).map((colKey) => {
                if (colKey === "ItemName") return (
                  <View key={colKey} style={colItem as any}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress("ItemName")}>
                      <Text style={styles.headerCell}>Item Name{sortIndicator("ItemName")}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, "ItemName", colWidths.ItemName) } as any} />}
                  </View>
                );
                if (colKey === "Customer") return (
                  <View key={colKey} style={colCustomer as any}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress("Customer")}>
                      <Text style={styles.headerCell}>Customer{sortIndicator("Customer")}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, "Customer", colWidths.Customer) } as any} />}
                  </View>
                );
                if (colKey === "SlpName") return (
                  <View key={colKey} style={colSlp as any}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress("SlpName")}>
                      <Text style={styles.headerCell}>Sales Person{sortIndicator("SlpName")}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, "SlpName", colWidths.SlpName) } as any} />}
                  </View>
                );
                if (colKey === "Total") return (
                  <View key={colKey} style={[styles.colTotalBase, { width: colWidths.Total }, hiddenCols.includes("Total") && { display: "none" as const }]}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress("Total")}>
                      <Text style={[styles.headerCell, styles.monthAlign]}>Total{sortIndicator("Total")}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, "Total", colWidths.Total) } as any} />}
                  </View>
                );
                return (
                  <View key={colKey} style={[styles.colMonthBase, { width: colWidths[colKey] }, hiddenCols.includes(colKey) && { display: "none" as const }]}>
                    <TouchableOpacity style={{ flex: 1 }} onPress={() => onSortPress(colKey)}>
                      <Text style={[styles.headerCell, styles.monthAlign]}>{colKey}{sortIndicator(colKey)}</Text>
                    </TouchableOpacity>
                    {Platform.OS === "web" && <View style={rszStyle} {...{ onMouseDown: (e: any) => startResize(e, colKey, colWidths[colKey]) } as any} />}
                  </View>
                );
              })}
            </Animated.View>
          </View>

          {/* ── Scrollable data rows ──────────────────────────────────────── */}
          <View style={{ flex: 1 }} onLayout={onDataAreaLayout}>
            <ScrollView
              horizontal
              onScroll={onBodyHScroll}
              scrollEventThrottle={16}
              showsHorizontalScrollIndicator={false}
            >
              <ScrollView
                style={{ height: dataAreaHeight }}
                nestedScrollEnabled
                showsVerticalScrollIndicator
              >
                <View style={{ width: tableWidth }}>
                  {pageRows.map((row, idx) => {
                    const rowKey = (safePage - 1) * pageSize + idx;
                    const expanded = expandedRow === rowKey;
                    const rowSum = getRowTotal(row);
                    return (
                      <View key={rowKey} style={[styles.tableRow, idx % 2 === 1 && styles.altRow]}>
                        {getOrderedKeys(allColOptions.map(c => c.value)).map((colKey) => {
                          if (colKey === "ItemName") return (
                            <CellWrapper key={colKey} style={[colItem as any, copiedKey === `${rowKey}-item` ? styles.copiedCell : undefined]} onLongPress={() => copyCell(`${rowKey}-item`, row.ItemName)}>
                              <Text selectable style={[styles.cell, styles.expandableCell]} numberOfLines={expanded ? undefined : 1} onPress={() => toggleRow(rowKey)}>
                                {row.ItemName}
                              </Text>
                            </CellWrapper>
                          );
                          if (colKey === "Customer") return (
                            <CellWrapper key={colKey} style={[colCustomer as any, copiedKey === `${rowKey}-cust` ? styles.copiedCell : undefined]} onLongPress={() => copyCell(`${rowKey}-cust`, row.Customer)}>
                              <Text selectable style={[styles.cell, styles.expandableCell]} numberOfLines={expanded ? undefined : 1} onPress={() => toggleRow(rowKey)}>
                                {row.Customer}
                              </Text>
                            </CellWrapper>
                          );
                          if (colKey === "SlpName") return (
                            <CellWrapper key={colKey} style={[colSlp as any, copiedKey === `${rowKey}-slp` ? styles.copiedCell : undefined]} onLongPress={() => copyCell(`${rowKey}-slp`, row.SlpName)}>
                              <Text selectable style={[styles.cell, styles.expandableCell]} numberOfLines={expanded ? undefined : 1} onPress={() => toggleRow(rowKey)}>
                                {row.SlpName}
                              </Text>
                            </CellWrapper>
                          );
                          if (colKey === "Total") return (
                            <CellWrapper key={colKey} style={[styles.colTotalBase, { width: colWidths.Total }, copiedKey === `${rowKey}-tot` ? styles.copiedCell : undefined, hiddenCols.includes("Total") && { display: "none" as const }]} onLongPress={() => copyCell(`${rowKey}-tot`, String(rowSum))}>
                              <Text selectable style={[styles.cell, styles.rowTotalText]}>
                                {rowSum.toLocaleString()}
                              </Text>
                            </CellWrapper>
                          );
                          const val = (row[colKey as keyof valueL36MModel] as number) || 0;
                          const ck = `${rowKey}-${colKey}`;
                          return (
                            <CellWrapper key={colKey} style={[styles.colMonthBase, { width: colWidths[colKey] }, copiedKey === ck ? styles.copiedCell : undefined, hiddenCols.includes(colKey) && { display: "none" as const }]} onLongPress={() => copyCell(ck, String(val))}>
                              <Text selectable style={[styles.cell, styles.monthAlign]}>
                                {val.toLocaleString()}
                              </Text>
                            </CellWrapper>
                          );
                        })}
                      </View>
                    );
                  })}
                </View>
              </ScrollView>
            </ScrollView>
          </View>

          {/* ── Frozen totals row ─────────────────────────────────────────── */}
          <View style={{ overflow: "hidden" }}>
            <Animated.View
              style={[styles.totalsRow, { width: tableWidth, transform: [{ translateX: negScrollAnim }] }]}
            >
              {getOrderedKeys(allColOptions.map(c => c.value)).map((colKey) => {
                if (colKey === "ItemName") return (
                  <View key={colKey} style={colItem}>
                    <Text style={styles.totalCell}>TOTAL</Text>
                  </View>
                );
                if (colKey === "Customer") return (
                  <View key={colKey} style={colCustomer}>
                    <Text style={[styles.totalCell, styles.totalSubLabel]}>
                      {filtered.length.toLocaleString()} filtered rows
                    </Text>
                  </View>
                );
                if (colKey === "SlpName") return (
                  <View key={colKey} style={colSlp} />
                );
                if (colKey === "Total") return (
                  <CellWrapper
                    key={colKey}
                    style={[styles.colTotalBase, { width: colWidths.Total }, copiedKey === "ttl-Total" ? styles.copiedCell : undefined, hiddenCols.includes("Total") && { display: "none" as const }]}
                    onLongPress={() => copyCell("ttl-Total", String(colTotals["Total"] || 0))}
                  >
                    <Text selectable style={[styles.totalCell, styles.monthAlign]}>
                      {(colTotals["Total"] || 0).toLocaleString()}
                    </Text>
                  </CellWrapper>
                );
                const ck = `ttl-${colKey}`;
                return (
                  <CellWrapper
                    key={colKey}
                    style={[styles.colMonthBase, { width: colWidths[colKey] }, copiedKey === ck ? styles.copiedCell : undefined, hiddenCols.includes(colKey) && { display: "none" as const }]}
                    onLongPress={() => copyCell(ck, String(colTotals[colKey] || 0))}
                  >
                    <Text selectable style={[styles.totalCell, styles.monthAlign]}>
                      {(colTotals[colKey] || 0).toLocaleString()}
                    </Text>
                  </CellWrapper>
                );
              })}
            </Animated.View>
          </View>

          {/* Pagination */}
          <View style={styles.paginationRow}>
            <Text style={styles.paginationInfo}>
              {filtered.length === 0
                ? "No results"
                : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)} of ${filtered.length.toLocaleString()}`}
            </Text>
            <View style={styles.paginationBtns}>
              <TouchableOpacity onPress={() => goToPage(1)} disabled={safePage === 1} style={[styles.pageBtn, safePage === 1 && styles.pageBtnDisabled]}>
                <Text style={styles.pageBtnTxt}>«</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => goToPage(safePage - 1)} disabled={safePage === 1} style={[styles.pageBtn, safePage === 1 && styles.pageBtnDisabled]}>
                <Text style={styles.pageBtnTxt}>‹</Text>
              </TouchableOpacity>
              <Text style={styles.pageIndicator}>Page {safePage} / {totalPages}</Text>
              <TouchableOpacity onPress={() => goToPage(safePage + 1)} disabled={safePage === totalPages} style={[styles.pageBtn, safePage === totalPages && styles.pageBtnDisabled]}>
                <Text style={styles.pageBtnTxt}>›</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => goToPage(totalPages)} disabled={safePage === totalPages} style={[styles.pageBtn, safePage === totalPages && styles.pageBtnDisabled]}>
                <Text style={styles.pageBtnTxt}>»</Text>
              </TouchableOpacity>
            </View>
          </View>

        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#f5f5f5" },
  loadingContainer: { flex: 1, justifyContent: "center", alignItems: "center" },
  loadingText: { marginTop: 12, fontSize: 14, color: "#555" },

  frozenHeader: { backgroundColor: "#f5f5f5", padding: 16, paddingBottom: 0, zIndex: 1000 },
  pageTitle: { fontSize: 14, fontWeight: "600", color: "#333", marginBottom: 2 },
  currencyIndicator: { fontSize: 11, fontStyle: "italic", color: "#888", marginBottom: 2 },
  fetchedAt: { fontSize: 11, fontStyle: "italic", color: "#888", marginBottom: 6 },
  searchInput: {
    borderWidth: 1, borderColor: "#ddd", borderRadius: 6,
    paddingHorizontal: 10, paddingVertical: 6,
    fontSize: 13, backgroundColor: "#fff", height: 32,
    width: 320, marginLeft: 0, marginRight: 16,
  },
  filterPanel: {
    backgroundColor: "#fff", borderRadius: 8, padding: 14, marginBottom: 16,
    shadowColor: "#000", shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08, shadowRadius: 3, elevation: 2,
    zIndex: 1000,
  },
  filterHeaderRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  filterHeading: {
    fontSize: 12, fontWeight: "600", color: "#888",
    textTransform: "uppercase", letterSpacing: 0.5,
  },
  resetBtn: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 4, backgroundColor: "#f0f0f0" },
  resetBtnText: { fontSize: 11, color: "#555", fontWeight: "600" as const },
  filterGrid: { flexDirection: "row", flexWrap: "wrap", gap: 12, zIndex: 1000 },

  tableSection: { flex: 1, padding: 8, paddingTop: 0, paddingBottom: 8 },
  tableCard: {
    flex: 1, backgroundColor: "#fff", borderRadius: 8, padding: 16,
    shadowColor: "#000", shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08, shadowRadius: 3, elevation: 2,
  },

  tableTopBar: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 },
  tableTitle: { fontSize: 15, fontWeight: "600", color: "#333" },
  pageSizeRow: { flexDirection: "row", alignItems: "center", gap: 6 },
  pageSizeLabel: { fontSize: 12, color: "#555" },
  pageSizeBtn: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 5, borderWidth: 1, borderColor: "#ddd", backgroundColor: "#fff" },
  pageSizeBtnActive: { backgroundColor: "#1976d2", borderColor: "#1976d2" },
  pageSizeTxt: { fontSize: 12, color: "#333" },
  pageSizeTxtActive: { color: "#fff", fontWeight: "600" },
  resetSortBtn: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 5, backgroundColor: "#fff0f0", borderWidth: 1, borderColor: "#ffcdd2", marginRight: 8 },
  resetSortTxt: { fontSize: 12, color: "#d32f2f", fontWeight: "600" },

  tableHeader: { flexDirection: "row", backgroundColor: "#f0f4f8", borderTopLeftRadius: 4, borderTopRightRadius: 4, borderBottomWidth: 2, borderBottomColor: "#dde3ea" },
  tableRow: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#f0f0f0" },
  altRow: { backgroundColor: "#fafafa" },
  totalsRow: { flexDirection: "row", backgroundColor: "#e3f2fd", borderTopWidth: 2, borderTopColor: "#1976d2" },

  headerCell: { fontWeight: "700", color: "#444", fontSize: 11 },
  cell: { fontSize: 11, color: "#333" },
  expandableCell: { color: "#1976d2" },
  monthAlign: { textAlign: "right" },
  rowTotalText: { fontWeight: "600", color: "#1b5e20", textAlign: "right" },
  totalCell: { fontSize: 11, fontWeight: "700", color: "#0d47a1" },
  totalSubLabel: { fontSize: 10, fontWeight: "400", color: "#888" },

  copiedCell: { backgroundColor: "#c8e6c9" },

  colBase: { paddingHorizontal: 8, paddingVertical: 6, borderRightWidth: 1, borderRightColor: "#eee" },
  colMonthBase: { paddingHorizontal: 4, paddingVertical: 6, borderRightWidth: 1, borderRightColor: "#eee" },
  colTotalBase: { paddingHorizontal: 4, paddingVertical: 6, borderRightWidth: 1, borderRightColor: "#90caf9", backgroundColor: "#e8f0fe" },

  paginationRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginTop: 8, flexWrap: "wrap", gap: 8 },
  paginationInfo: { fontSize: 12, color: "#666" },
  paginationBtns: { flexDirection: "row", alignItems: "center", gap: 4 },
  pageBtn: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 5, borderWidth: 1, borderColor: "#ddd", backgroundColor: "#fff" },
  pageBtnDisabled: { borderColor: "#eee", backgroundColor: "#f5f5f5" },
  pageBtnTxt: { fontSize: 14, color: "#333" },
  pageIndicator: { fontSize: 12, color: "#555", marginHorizontal: 8 },
});
